import React from 'react'
import { DocumentNode } from 'graphql'
import { ApolloConsumer } from 'react-apollo'
import { CollectionQuery } from './collection-query'
import { Order } from '../../legacy-design-system/order'
import { AggregationOptions } from '../aggregations'
import ApolloClient from 'apollo-client'

export interface CollectionVariables {
  readonly organizationID: string
  readonly offset: number
  readonly limit: number
  readonly orderBy: {
    readonly field: string
    readonly direction: string
  }
  readonly filterText?: string
  readonly quickFilter?: string
  readonly [key: string]: any
}

interface ChildProps<TData, TAdditionalProps = {}> {
  readonly items: TData[]
  readonly total: number
  readonly loading: boolean
  readonly loadMore: () => Promise<any>
  readonly refresh: () => Promise<any>
  readonly organizationID: string
  readonly additionalProps: TAdditionalProps
  readonly apolloClient: ApolloClient<any>
  readonly globalTotal?: number
}

interface Props<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string,
  TAdditionalProps
> {
  query: DocumentNode
  limit: number
  dataKey: string
  organizationID: string
  order: Order<TOrderField>
  filterText?: string
  quickFilterID?: string
  applicationID?: string
  libraryHash?: string
  aggregationFilters?: AggregationOptions<TAggregationCategory>
  licensedOnly?: boolean
  isGroupBySinkEnabled?: boolean
  additionalFilters?: { [key: string]: any }
  areVariablesEqual?: (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => boolean
  children: (props: ChildProps<TData, TAdditionalProps>) => React.ReactNode
}

export class OrganizationCollectionLoader<
  TData,
  TOrderField extends string,
  TAggregationCategory extends string,
  TAdditionalProps = {}
> extends React.PureComponent<
  Props<TData, TOrderField, TAggregationCategory, TAdditionalProps>
> {
  public render() {
    const {
      query,
      aggregationFilters,
      additionalFilters,
      areVariablesEqual,
      organizationID,
      dataKey,
      limit,
      order,
      filterText,
      quickFilterID,
      applicationID,
      libraryHash,
      licensedOnly,
      isGroupBySinkEnabled,
      children
    } = this.props

    const variables = Object.assign({}, aggregationFilters, {
      organizationID,
      offset: 0,
      limit,
      orderBy: order,
      filterText,
      quickFilter: quickFilterID,
      applicationID,
      libraryHash,
      licensedOnly,
      groupedBy: isGroupBySinkEnabled ? 'SINK' : undefined,
      ...additionalFilters
    }) as CollectionVariables

    return (
      <ApolloConsumer>
        {(client) => {
          return (
            <CollectionQuery<TData>
              query={query}
              variables={variables}
              areVariablesEqual={areVariablesEqual}
              dataKey={dataKey}
              client={client}
            >
              {({ loading, data, loadMore, refetch }) => {
                const collection = data || {}
                const {
                  items = [],
                  total = 0,
                  globalTotal,
                  ...props
                } = collection

                return children({
                  total,
                  items,
                  globalTotal,
                  loading,
                  organizationID,
                  loadMore,
                  refresh: refetch,
                  additionalProps: props as TAdditionalProps,
                  apolloClient: client
                })
              }}
            </CollectionQuery>
          )
        }}
      </ApolloConsumer>
    )
  }
}
